/**
 * ESC/POS commands for thermal receipt printers (Epson-compatible: most 58/80 mm printers sold in India).
 * Text is sent as plain ASCII: the rupee sign becomes "Rs." and other characters the printer's code page
 * can't show become "?", so names in other scripts print as their English name where one exists.
 */
const ESC = 0x1b;
const GS = 0x1d; // group separator command prefix (not the GS1 FNC1 character)

/** Characters per line in the printer's default font A. */
export const charsPerLine = (paperMm: 58 | 80) => (paperMm === 58 ? 32 : 48);

export function asciiText(s: string): string {
  return s
    .replace(/₹\s?/g, 'Rs.')
    .replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—−]/g, '-').replace(/×/g, 'x').replace(/·/g, '-').replace(/…/g, '...')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^\x20-\x7e\n]/g, '?');
}

/** Word-wraps to `width`; long words are split. */
export function wrap(s: string, width: number): string[] {
  const out: string[] = [];
  for (const para of s.split('\n')) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      let w = word;
      while (w.length > width) {
        if (line) { out.push(line); line = ''; }
        out.push(w.slice(0, width));
        w = w.slice(width);
      }
      if (!line) line = w;
      else if (line.length + 1 + w.length <= width) line += ' ' + w;
      else { out.push(line); line = w; }
    }
    out.push(line);
  }
  return out;
}

export class EscPos {
  private out: number[] = [];
  constructor(readonly width: number) {
    this.raw(ESC, 0x40); // initialise
  }
  raw(...bytes: number[]) { this.out.push(...bytes); return this; }
  private str(s: string) { for (const ch of asciiText(s)) this.out.push(ch.charCodeAt(0)); return this; }
  align(a: 'left' | 'center' | 'right') { return this.raw(ESC, 0x61, a === 'left' ? 0 : a === 'center' ? 1 : 2); }
  bold(on: boolean) { return this.raw(ESC, 0x45, on ? 1 : 0); }
  /** Double width and height (used for the store name and total). */
  big(on: boolean) { return this.raw(GS, 0x21, on ? 0x11 : 0x00); }
  /** Wrapped text, one or more lines. Width halves while `big` is on, so pass `width` then. */
  text(s: string, width = this.width) {
    for (const l of wrap(asciiText(s), width)) this.str(l).raw(0x0a);
    return this;
  }
  /** Left text and right-aligned value on one line (left side wraps if it doesn't fit). */
  pair(left: string, right: string, width = this.width) {
    const r = asciiText(right);
    const room = Math.max(1, width - r.length - 1);
    const lines = wrap(asciiText(left), room);
    lines.forEach((l, i) => {
      if (i < lines.length - 1) this.str(l).raw(0x0a);
      else this.str(l.padEnd(width - r.length, ' ') + r).raw(0x0a);
    });
    return this;
  }
  rule(ch = '-') { return this.str(ch.repeat(this.width)).raw(0x0a); }
  feed(lines = 1) { return this.raw(ESC, 0x64, Math.min(255, lines)); }
  /** Feed past the tear bar and cut (printers without a cutter ignore it). */
  cut() { return this.raw(GS, 0x56, 0x42, 0x03); }
  /** Pulse pin 2 to open a cash drawer connected to the printer's RJ11 port. */
  drawer() { return this.raw(ESC, 0x70, 0x00, 0x19, 0xfa); }
  /** QR code (model 2), e.g. a UPI payment link. */
  qr(data: string, size = 6) {
    const d = Array.from(new TextEncoder().encode(data));
    const len = d.length + 3;
    return this
      .raw(GS, 0x28, 0x6b, 4, 0, 0x31, 0x41, 0x32, 0x00)
      .raw(GS, 0x28, 0x6b, 3, 0, 0x31, 0x43, Math.max(1, Math.min(16, size)))
      .raw(GS, 0x28, 0x6b, 3, 0, 0x31, 0x45, 0x31)
      .raw(GS, 0x28, 0x6b, len & 0xff, len >> 8, 0x31, 0x50, 0x30, ...d)
      .raw(GS, 0x28, 0x6b, 3, 0, 0x31, 0x51, 0x30);
  }
  bytes(): Uint8Array { return Uint8Array.from(this.out); }
}

/** Only the drawer pulse, for "Open drawer" without printing. */
export const drawerKick = () => new EscPos(32).drawer().bytes();
